import { useEffect, useMemo, useState } from "react";
import api from "../../api";
import "./Portfolio.css";

const DEFAULT_FILTERS = ["Projets", "Stack technique"];
const PORTFOLIO_PAGE_SLUG = "portfolio";
const FALLBACK_DESCRIPTION =
  "Explore mon parcours ? travers des projets soigneusement con?us pour r?soudre des probl?mes r?els et illustrer ma ma?trise des interfaces modernes.";

const STACK_ICONS = [
  {
    name: "HTML5",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
  },
  {
    name: "CSS3",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg",
  },
  {
    name: "Sass",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sass/sass-original.svg",
  },
  {
    name: "JavaScript",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
  },
  {
    name: "React",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
  },
  {
    name: "Node.js",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
  },
  {
    name: "Python",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
  },
  {
    name: "Symfony",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/symfony/symfony-original.svg",
    invertOnDark: true,
  },
  {
    name: "Laravel",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg",
  },
  {
    name: "Docker",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
  },
  {
    name: "WordPress",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-plain.svg",
  },
  {
    name: "Tailwind CSS",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg",
  },
  {
    name: "Git",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
  },
  {
    name: "GitHub",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
    invertOnDark: true,
  },
  {
    name: "Figma",
    icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
  },
];

const TECHNOLOGY_TAXONOMIES = ["technologies", "technology", "tech", "technologie"];
const KEY_FEATURE_TAXONOMIES = [
  "key_features",
  "key-features",
  "key_feature",
  "feature",
  "features",
  "fonctionnalites",
  "fonctionnalit?s",
];

const extractFilterLabels = (meta) => {
  const rawFilters = meta?.acf?.portfolio_filters;
  if (!Array.isArray(rawFilters)) return DEFAULT_FILTERS;

  const normalized = rawFilters
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (typeof entry?.label === "string") return entry.label;
      if (typeof entry?.title === "string") return entry.title;
      if (typeof entry?.value === "string") return entry.value;
      return "";
    })
    .map((label) => label.trim())
    .filter(Boolean);

  return normalized.length ? normalized : DEFAULT_FILTERS;
};

const getProjectMediaUrl = (project) => {
  if (!project) return null;
  const featured = project._embedded?.["wp:featuredmedia"];
  const firstMedia = featured && featured.length ? featured[0] : null;

  return (
    firstMedia?.media_details?.sizes?.medium?.source_url ||
    firstMedia?.source_url ||
    project.featured_media_src_url ||
    project.jetpack_featured_media_url ||
    null
  );
};

const decodeHtmlEntities = (value) => {
  if (!value) return "";

  if (typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<!doctype html><body>${value}`, "text/html");
    return doc.body.textContent || "";
  }

  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const sanitizeText = (value) => {
  if (!value) return "";
  const stripped = value.replace(/<\/?[^>]+(>|$)/g, "");
  return decodeHtmlEntities(stripped).trim();
};

const parseKeyFeatures = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map(sanitizeText).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(/[\r\n]+/)
      .map((fragment) => sanitizeText(fragment))
      .filter(Boolean);
  }
  return [];
};

const getFeaturePoints = (value) => {
  const clean = sanitizeText(value);
  if (!clean) {
    return ["Design r?fl?chi", "Exp?rience fluide", "Responsive et performant"];
  }

  return clean
    .split(/[.!?]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 4);
};

const extractTaxonomyTechnologies = (project) => {
  const terms = project._embedded?.["wp:term"];
  if (!terms) return [];

  return terms
    .flat()
    .filter(
      (term) =>
        term?.taxonomy &&
        TECHNOLOGY_TAXONOMIES.includes(term.taxonomy.toLowerCase())
    )
    .map((term) => term.name)
    .filter(Boolean);
};

const extractKeyFeatureTerms = (project) => {
  const terms = project._embedded?.["wp:term"];
  if (!terms) return [];

  return terms
    .flat()
    .filter(
      (term) =>
        term?.taxonomy &&
        KEY_FEATURE_TAXONOMIES.includes(term.taxonomy.toLowerCase())
    )
    .map((term) => term.name)
    .filter(Boolean);
};

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [portfolioMeta, setPortfolioMeta] = useState(null);
  const [activeTab, setActiveTab] = useState(DEFAULT_FILTERS[0]);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    api
      .get(`/pages?slug=${PORTFOLIO_PAGE_SLUG}&_embed=1`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) {
          setPortfolioMeta(res.data[0]);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    api
      .get("/posts?_embed=1")
      .then((res) => setProjects(res.data))
      .catch((err) => console.log(err));
  }, []);

  const dynamicFilters = useMemo(
    () => extractFilterLabels(portfolioMeta),
    [portfolioMeta]
  );

  const projectTabLabel = dynamicFilters[0];
  const stackIcons = STACK_ICONS;

  useEffect(() => {
    if (!dynamicFilters.length) return;
    setActiveTab((prev) =>
      dynamicFilters.includes(prev) ? prev : dynamicFilters[0]
    );
  }, [dynamicFilters]);

  const detailHeroImage = useMemo(() => {
    if (!selectedProject) return null;
    return getProjectMediaUrl(selectedProject);
  }, [selectedProject]);

  const detailFeatures = useMemo(() => {
    if (!selectedProject) return [];
    const custom = parseKeyFeatures(selectedProject.acf?.key_features);
    if (custom.length) return custom;

    const taxonomyFeatures = extractKeyFeatureTerms(selectedProject);
    if (taxonomyFeatures.length) return taxonomyFeatures;

    return getFeaturePoints(selectedProject.excerpt?.rendered);
  }, [selectedProject]);

  const detailTechs = useMemo(() => {
    if (!selectedProject) return [];

    const fallback = selectedProject.acf?.technologies?.filter(Boolean) ?? [];
    const taxonomyTechs = extractTaxonomyTechnologies(selectedProject);

    if (taxonomyTechs.length) return taxonomyTechs;
    if (fallback.length) return fallback;
    return [];
  }, [selectedProject]);

  return (
    <section id="portfolio" className="portfolio-section">
      <div className="container">
        <div className="portfolio-showcase reveal-up">
          <h2 className="portfolio-title">
            {sanitizeText(
              portfolioMeta?.acf?.portfolio_title ?? portfolioMeta?.title?.rendered
            ) || "Portfolio Showcase"}
          </h2>
          <p className="portfolio-description">
            {sanitizeText(
              portfolioMeta?.acf?.portfolio_description ??
                portfolioMeta?.excerpt?.rendered
            ) || FALLBACK_DESCRIPTION}
          </p>
        </div>

        <div className="portfolio-tabs-wrapper mb-5 reveal-up">
          <div className="portfolio-tabs">
            {dynamicFilters.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeTab === projectTabLabel ? (
          <div className="portfolio-grid">
            {projects.length === 0 ? (
              <div className="text-center text-muted">Chargement des projets...</div>
            ) : (
              projects.map((project) => {
                const mediaUrl = getProjectMediaUrl(project);

                return (
                  <article key={project.id} className="project-card modern-card">
                    <div
                      className="project-visual"
                      style={
                        mediaUrl ? { backgroundImage: `url(${mediaUrl})` } : undefined
                      }
                      aria-hidden
                    />
                    <div className="project-card-body">
                      <h5
                        dangerouslySetInnerHTML={{
                          __html: project.title?.rendered ?? "Sans titre",
                        }}
                      />
                      <p
                        className="clamp"
                        dangerouslySetInnerHTML={{
                          __html: project.excerpt?.rendered ?? "",
                        }}
                      />
                    </div>
                    <div className="project-card-footer">
                      <div className="project-card-actions single">
                        <button
                          type="button"
                          className="btn primary secondary-action"
                          onClick={() => setSelectedProject(project)}
                        >
                          Details
                          <span className="icon icon-arrow" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        ) : (
          <div className="stack-icons-grid">
            {stackIcons.length === 0 ? (
              <div className="text-center text-muted">
                Contenu du stack technique indisponible pour le moment.
              </div>
            ) : (
              stackIcons.map((tech) => (
                <div key={tech.name} className="stack-icon-item">
                  <div className="stack-icon-badge">
                    <img
                      src={tech.icon}
                      alt={tech.name}
                      loading="lazy"
                      className={tech.invertOnDark ? "icon-invert-on-dark" : ""}
                    />
                  </div>
                  <span>{tech.name}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {selectedProject && (
        <div
          className="project-detail-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`D?tails du projet ${sanitizeText(
            selectedProject.title?.rendered
          )}`}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedProject(null);
            }
          }}
        >
          <div className="project-detail-card">
            <div className="detail-header">
              <button
                type="button"
                className="detail-back"
                onClick={() => setSelectedProject(null)}
              >
                Retour
              </button>
              <div className="detail-breadcrumb">
                Projects &gt; {sanitizeText(selectedProject.title?.rendered)}
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-left">
                <h3
                  className="detail-title"
                  dangerouslySetInnerHTML={{
                    __html: selectedProject.title?.rendered ?? "",
                  }}
                />
                <p>{sanitizeText(selectedProject.excerpt?.rendered)}</p>

                <div className="detail-stats">
                  <div className="detail-stat">
                    <span>{detailTechs.length}</span>
                    <p>Technologies</p>
                  </div>
                  <div className="detail-stat">
                    <span>{detailFeatures.length}</span>
                    <p>Fonctionnalit?s</p>
                  </div>
                </div>

                <div className="detail-actions">
                  {selectedProject.acf?.github && (
                    <a
                      href={selectedProject.acf.github}
                      target="_blank"
                      rel="noreferrer"
                      className="btn secondary"
                    >
                      <span className="github-mark" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path
                            fill="currentColor"
                            d="M12 2C6.48 2 2 6.59 2 12.25c0 4.53 2.87 8.37 6.84 9.73.5.1.68-.22.68-.49 0-.24-.01-1.04-.01-1.88-2.78.62-3.37-1.21-3.37-1.21-.45-1.18-1.11-1.49-1.11-1.49-.91-.64.07-.63.07-.63 1 .08 1.53 1.06 1.53 1.06.9 1.57 2.35 1.12 2.92.86.09-.67.35-1.12.63-1.37-2.22-.26-4.56-1.15-4.56-5.11 0-1.13.39-2.05 1.03-2.78-.1-.26-.45-1.31.1-2.74 0 0 .84-.28 2.75 1.06A9.3 9.3 0 0 1 12 6.83c.85 0 1.71.12 2.51.35 1.91-1.34 2.75-1.06 2.75-1.06.55 1.43.2 2.48.1 2.74.64.73 1.03 1.65 1.03 2.78 0 3.97-2.34 4.85-4.57 5.11.36.32.68.95.68 1.92 0 1.39-.01 2.5-.01 2.84 0 .27.18.59.69.49A10.26 10.26 0 0 0 22 12.25C22 6.59 17.52 2 12 2Z"
                          />
                        </svg>
                      </span>
                      GitHub
                    </a>
                  )}
                </div>
              </div>
              <div className="detail-right">
                <div
                  className="detail-hero"
                  style={
                    detailHeroImage
                      ? { backgroundImage: `url(${detailHeroImage})` }
                      : undefined
                  }
                />
                <div className="detail-keyfeatures">
                  <h4>Key Features</h4>
                  {detailFeatures.length === 0 ? (
                    <p className="text-muted">
                      Aucune fonctionnalit? mise en avant pour ce projet.
                    </p>
                  ) : (
                    <ul>
                      {detailFeatures.map((feature, index) => (
                        <li key={`${feature}-${index}`}>{feature}</li>
                      ))}
                    </ul>
                  )}

                  <h4>Technologies utilis?es</h4>
                  <div className="detail-tech-tags">
                    {detailTechs.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
